<script setup>
import { LControlLayers, LGeoJson, LMap, LTileLayer, LImageOverlay } from "@vue-leaflet/vue-leaflet"
import "leaflet/dist/leaflet.css"
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import LFullScreen from "vue-leaflet-fullscreen"
import "vue-leaflet-fullscreen/dist/style.css"
import axios from '@/helpers/axios'
import Loader from '@/views/Loader.vue'
import moment from "moment";
import ChartJsLineChart from '@/views/satellite/ChartJsLineChart.vue'
import Breadcrumbs from '@/views/general/Breadcrumbs.vue'
import SoilCard from '@/views/satellite/SoilCard.vue'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'

definePage({
  meta: {
    layout: 'default',
    role: 'user'
  },
})

const { t } = useI18n()
const route = useRoute()
const uuid = route.params.uuid
const webcam = ref(null)
const images = ref([])
const selectedImage = ref(null)
const loading = ref(true)
const routeLastData = route.query?.last_data
const selectedDate = ref(routeLastData && moment(routeLastData).isValid()
  ? moment(routeLastData).format('YYYY-MM-DD')
  : moment().format('YYYY-MM-DD'))
const menu = ref(false)
const hasAppliedLastData = ref(false)

const getData = () => {
  loading.value = true
  axios.get('/webcams/' + uuid, {
    params: { date: selectedDate.value }, // pass date as query param
  }).then(response => {
    webcam.value = response.data
    images.value = response.data.images

    selectedImage.value = images.value.length > 0 ? images.value[0] : null

    if (!hasAppliedLastData.value && response.data.last_data) {
      const latestDate = moment(response.data.last_data).format('YYYY-MM-DD')
      if (selectedDate.value !== latestDate) {
        selectedDate.value = latestDate
      }
      hasAppliedLastData.value = true
    }
  }).catch(e => {
    console.error(e)
  }).finally(() => {
    loading.value = false
  })
}

getData()

watch(selectedDate, () => {
  getData()
})

const formattedDate = (date) => {
  if (!date) return ''
  return moment(date).format('HH:mm')
}

const onDateSelect = (value) => {
  selectedDate.value = moment(value).format('YYYY-MM-DD')
  menu.value = false
}
</script>

<template>
  <Loader v-if="loading" />

  <div v-if="loading === false && webcam === null">
    <div
      class="d-flex flex-column align-center justify-center text-center flex-grow-1 py-10"
    >
      <VIcon icon="tabler-camera-off" size="48" color="grey" class="mb-3" />
      <h4 class="text-h6 font-weight-medium text-high-emphasis mb-1">
        {{ $t('webcams.webcam_not_found') }}
      </h4>
    </div>
  </div>

  <div v-if="loading === false && webcam">
    <div class="page-header mb-4">
      <div class="d-flex flex-column flex-sm-row align-center justify-space-between gap-4">
        <div class="flex-grow-1">
          <h2 class="text-h4 font-weight-semibold mb-2 text-high-emphasis">
            {{ webcam.name }}
          </h2>
          <!--
          <p class="text-body-1 text-medium-emphasis mb-0">
            {{ $t('webcams.webcams_description') }}
          </p>
          -->
        </div>

        <div class="d-flex justify-end" style="min-width: 290px;">
          <VMenu
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
          >
            <template #activator="{ props }">
              <VTextField
                v-model="selectedDate"
                :label="$t('webcams.select_date')"
                readonly
                v-bind="props"
                prepend-inner-icon="tabler-calendar"
                style="max-width: 290px; width: 100%;"
              />
            </template>

            <VDatePicker
              v-model="selectedDate"
              hide-header
              :max="moment().format('YYYY-MM-DD')"
              @update:model-value="onDateSelect"
            />
          </VMenu>
        </div>
      </div>
    </div>


    <div v-if="loading === false && images.length === 0">
      <div
        class="d-flex flex-column align-center justify-center text-center flex-grow-1 py-10"
      >
        <VIcon icon="tabler-photo-off" size="48" color="grey" class="mb-3" />
        <h4 class="text-h6 font-weight-medium text-high-emphasis mb-1">
          {{ $t('webcams.no_images') }}
        </h4>
      </div>
    </div>

    <div>
      <VCard
        v-if="selectedImage"
        style="border-radius: 16px; position: relative; overflow: hidden;"
        elevation="2"
      >
        <VImg
          :src="selectedImage.image_url"
          alt="Webcam image"
          cover
          height="100%"
          width="100%"
        />

        <div
          class="pa-2"
          style="
            position: absolute;
            top: 8px;
            left: 8px;
            background-color: rgba(0, 0, 0, 0.55);
            color: white;
            border-radius: 8px;
            font-weight: 700;
            font-size: 1.3rem;
            backdrop-filter: blur(4px);
          "
        >
          {{ formattedDate(selectedImage.captured_at) }}
        </div>

      </VCard>
      <VRow v-if="images.length" class="mt-4">
        <VCol
          v-for="(item, i) in images"
          :key="item.uuid"
          cols="4"
          sm="4"
          md="3"
          lg="2"
        >
          <VCard
            style="border-radius: 16px; position: relative; height: 120px; overflow: hidden;"
            elevation="2"
            class="cursor-pointer"
            @click="selectedImage = item"
          >
            <VImg
              :src="item.thumbnail_url"
              alt="Webcam image"
              cover
              height="100%"
              width="100%"
            />

            <div
              class="pa-2"
              style="
                position: absolute;
                top: 8px;
                left: 8px;
                background-color: rgba(0, 0, 0, 0.55);
                color: white;
                border-radius: 8px;
                font-weight: 700;
                font-size: 0.9rem;
                backdrop-filter: blur(4px);
              "
            >
              {{ formattedDate(item.captured_at) }}
            </div>

            <VIcon
              v-if="selectedImage.uuid === item.uuid"
              icon="tabler-check"
              size="35"
              color="primary"
              style="
                position: absolute;
                bottom: 6px;
                right: 6px;
                border-radius: 50%;
                padding: 2px;
              "
            />
          </VCard>
        </VCol>
      </VRow>
    </div>
  </div>
</template>
